/**
 * Reglas de cumplimiento de la Ley 21.156 y su Reglamento D.S. 56/2020, Art. 5º.
 *
 * El artículo evalúa dos dimensiones independientes, y un espacio cumple sólo si
 * cumple ambas:
 *   - horizontal (inc. 1): todo punto de uso público a ≤ 150 m de un DEA;
 *   - vertical (inc. 2): cobertura por niveles en edificios en altura.
 *
 * ── La regla vertical ──────────────────────────────────────────────────────
 * Un DEA del CLIENTE cubre su nivel y **2 hacia arriba y 2 hacia abajo**. Un DEA
 * EXTERNO (de un tercero o arrendatario) cubre **sólo su propio nivel**: no se
 * puede comprometer al cliente con un equipo que no controla.
 *
 * El ±2 se mide en ADYACENCIA FÍSICA dentro del stack de niveles, no en
 * diferencia numérica. Entre N-1 y N1 hay un piso, aunque restar dé 2. Por eso
 * todo el motor trabaja sobre índices del arreglo ordenado y nunca sobre el
 * número del nivel.
 *
 * Sólo se exige cobertura vertical a partir de **4 niveles totales**, contando
 * subterráneos. Y sólo los niveles de USO PÚBLICO obligan: una bodega o una sala
 * de máquinas no genera brecha.
 *
 * Este módulo es puro a propósito: es lo que decide si a un cliente se le dice
 * "cumple" o "no cumple" frente a una obligación legal, así que tiene que ser
 * verificable sin abrir un navegador.
 */

import { buildingLevels, totalLevels } from './levels.js';

/** Alcance del DEA del cliente, en niveles hacia cada lado. */
export const VERTICAL_REACH = 2;

/** Mínimo de niveles totales para que la dimensión vertical sea exigible. */
export const MIN_LEVELS_FOR_VERTICAL = 4;

export const NORMA_REF = 'LEY 21.156 · REGLAMENTO D.S. 56/2020';

/** ¿A este edificio se le exige cobertura vertical? */
export function verticalApplies(building) {
  return totalLevels(building) >= MIN_LEVELS_FOR_VERTICAL;
}

/** Niveles de abajo hacia arriba: el orden físico del stack. */
export function levelsBottomUp(building) {
  return buildingLevels(building).slice().sort((a, b) => a - b);
}

/** ¿El nivel tiene uso público? Los que no, no obligan. */
export function isPublicLevel(building, level) {
  return !(building.noPub || []).includes(level);
}

/** ¿Hay un DEA del cliente en ese nivel? */
export function hasClientDea(building, level) {
  return (building.tiene || []).includes(level);
}

/** ¿Hay un DEA externo (de un tercero) en ese nivel? */
export function hasExternalDea(building, level) {
  return (building.ext || []).includes(level);
}

/** Cuántos DEA del cliente hay en ese nivel. Cero si no hay ninguno, mínimo 1 si hay. */
export function clientDeaCount(building, level) {
  if (!hasClientDea(building, level)) return 0;
  const declared = Number.parseInt((building.cant || {})[level], 10);
  return Math.max(1, declared || 1);
}

/** ¿Hay algún equipo en el nivel, propio o de tercero? */
export function hasAnyDea(building, level) {
  return hasClientDea(building, level) || hasExternalDea(building, level);
}

/**
 * ¿El nivel del índice `index` queda cubierto?
 *
 * Cubierto si hay un DEA del cliente a distancia ≤ 2 en el stack, o un DEA
 * externo exactamente en ese nivel.
 *
 * @param {boolean[]} client   Presencia de DEA del cliente por índice.
 * @param {boolean[]} external Presencia de DEA externo por índice.
 */
export function isLevelCovered(client, external, index) {
  const from = Math.max(0, index - VERTICAL_REACH);
  const to = Math.min(client.length - 1, index + VERTICAL_REACH);
  for (let i = from; i <= to; i++) {
    if (client[i]) return true;
  }
  return Boolean(external[index]);
}

const clientPresence = (building) =>
  levelsBottomUp(building).map((level) => hasClientDea(building, level));

const externalPresence = (building) =>
  levelsBottomUp(building).map((level) => hasExternalDea(building, level));

/**
 * Recorre de abajo hacia arriba y, donde un nivel público queda sin cubrir,
 * coloca ahí un PCP — que a su vez pasa a cubrir ±2.
 *
 * Ir de abajo hacia arriba no es arbitrario: garantiza que cada equipo colocado
 * aproveche todo su alcance hacia arriba, que es la dirección todavía sin
 * resolver. Empezar por el medio daría más equipos para el mismo resultado.
 *
 * @returns {number[]} Niveles donde hay que instalar.
 */
function solveVerticalGaps(building, initialClient) {
  const levels = levelsBottomUp(building);
  const client = initialClient.slice();
  const external = externalPresence(building);
  const toInstall = [];

  levels.forEach((level, index) => {
    if (!isPublicLevel(building, level)) return;
    if (isLevelCovered(client, external, index)) return;
    client[index] = true;
    toInstall.push(level);
  });

  return toInstall;
}

/** Niveles donde falta instalar un PCP, contando lo que ya hay. */
export function levelsToInstall(building) {
  if (!verticalApplies(building)) return [];
  return solveVerticalGaps(building, clientPresence(building));
}

/** Cuántos PCP faltan en el edificio. */
export function missingDeaCount(building) {
  return levelsToInstall(building).length;
}

/** DEA mínimos que exige la regla partiendo de cero. Alimenta el conteo REQUERIDOS. */
export function requiredVerticalDea(building) {
  if (!verticalApplies(building)) return 0;
  return solveVerticalGaps(
    building,
    levelsBottomUp(building).map(() => false),
  ).length;
}

/**
 * DEA del cliente REUBICABLES: los que al quitar UNO no dejan descubierto
 * ningún nivel público que ya estaba cubierto.
 *
 * Sirve para no ofrecerle al cliente "reubicá el que tenés" cuando moverlo
 * abriría un hueco nuevo. Un nivel con 2+ equipos siempre aporta al menos uno
 * reubicable: al sacar uno, el nivel sigue teniendo cliente.
 */
export function relocatableDeaCount(building) {
  const levels = levelsBottomUp(building);
  const client = clientPresence(building);
  const external = externalPresence(building);
  let relocatable = 0;

  levels.forEach((level, index) => {
    const count = clientDeaCount(building, level);
    if (!count) return;

    const without = client.slice();
    if (count <= 1) without[index] = false;

    const breaksSomething = levels.some(
      (other, j) =>
        isPublicLevel(building, other) &&
        isLevelCovered(client, external, j) &&
        !isLevelCovered(without, external, j),
    );
    if (!breaksSomething) relocatable += 1;
  });

  return relocatable;
}

/**
 * Acción correctiva del edificio, en lenguaje para el cliente.
 * Sólo ofrece reubicar cuando de verdad hay equipos reubicables.
 */
export function verticalCorrectiveAction(building) {
  const missing = missingDeaCount(building);
  if (!missing) return '';

  const relocatable = relocatableDeaCount(building);
  const brandNew = Math.max(0, missing - relocatable);
  const plural = (n) => (n !== 1 ? 's' : '');

  if (relocatable >= missing) {
    return `reubicar ${missing} PCP existente${plural(missing)} o instalar ${missing} PCP nuevo${plural(missing)}`;
  }
  if (relocatable > 0) {
    return `instalar ${brandNew} PCP nuevo${plural(brandNew)} y reubicar ${relocatable} PCP existente${plural(relocatable)}`;
  }
  return `instalar ${missing} PCP nuevo${plural(missing)}`;
}

// ─────────────────────────────── Dimensión horizontal ───────────────────────

/**
 * Cumplimiento horizontal (inc. 1).
 *
 * OJO — comportamiento heredado: cuando `coveragePct` es `null` (no hay
 * perímetro trazado, o no hay ningún DEA) devuelve `true`. Un recinto sin datos
 * se declara conforme. Está pineado con un test y documentado en
 * docs/hallazgos.md como decisión pendiente: la obligación de la ley es por
 * sede, y un "cumple" por falta de medición no es un cumple.
 */
export function horizontalCompliant(coveragePct) {
  return coveragePct == null ? true : coveragePct >= 100;
}

/** Cumplimiento vertical (inc. 2): ningún edificio con niveles descubiertos. */
export function verticalCompliant(buildings) {
  return buildings.every((building) => missingDeaCount(building) === 0);
}

/** ¿Cumple el espacio en ambas dimensiones? */
export function isCompliant(coveragePct, buildings) {
  return complianceGaps(coveragePct, buildings).length === 0;
}

/**
 * Brechas concretas para llegar a cumplir.
 *
 * @param {number|null} coveragePct
 * @param {Building[]} buildings
 * @param {{canRelocateHorizontal?: boolean}} options
 *   `canRelocateHorizontal` indica que, reubicando los DEA que ya hay, se
 *   alcanza el 100%. Cambia el texto: no es lo mismo pedirle al cliente que
 *   compre un equipo que pedirle que mueva el que ya tiene.
 */
export function complianceGaps(coveragePct, buildings, options = {}) {
  const gaps = [];

  if (coveragePct != null && coveragePct < 100) {
    gaps.push(
      options.canRelocateHorizontal
        ? `Ampliar cobertura horizontal (${coveragePct}%): instalar un punto cardioprotegido de terreno o reubicar el existente`
        : `Ampliar cobertura horizontal (${coveragePct}%): instalar punto cardioprotegido de terreno`,
    );
  }

  buildings.forEach((building) => {
    const levels = levelsToInstall(building);
    if (!levels.length) return;
    const action = verticalCorrectiveAction(building);
    gaps.push(
      `${building.nombre}: ${levels.length === 1 ? 'falta' : 'faltan'} ${levels.length} nivel${levels.length === 1 ? '' : 'es'} · N${levels.join(', N')} — ${action}`,
    );
  });

  return gaps;
}

// ─────────────────── Equipos compartidos con un DEA de terreno ──────────────

/**
 * ¿El nivel comparte su DEA con un equipo de terreno?
 *
 * No es lo mismo que un DEA externo: acá el equipo ES del cliente y ya está
 * contado en el plano como DEA de terreno, así que no puede sumarse dos veces
 * al total. Sí cubre normalmente, porque está instalado en ese nivel.
 */
export function isSharedLevel(building, level) {
  return Object.prototype.hasOwnProperty.call(building.compartidos || {}, level);
}

/** Número del DEA de terreno con el que ese nivel comparte equipo, si se declaró. */
export function sharedDeaNumber(building, level) {
  return (building.compartidos || {})[level];
}
